import * as React from 'react'
import { useAuth } from '@/auth/AuthContext'
import { useEvent } from '@/hooks/useEvents'
import { useVendorProfile, useVenueVendorServices } from '@/hooks/useVendors'
import { useSendQuoteRequest } from '@/hooks/useQuoteRequests'
import { requestErrorMessage } from '@/helpers/apiClient'
import { formatEventDate } from '@/lib/eventTime'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Facts } from '@/components/ui/facts'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { LoadingState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { createQuoteRequestInputSchema } from '@lib/input/quote-request.input'
import { QUOTE_MESSAGE_MAX } from '@lib/constants/quote-request'
import { offeringPriceLabel } from '@lib/helpers/vendor-offering'

/** A vendor company, as the dialog names it. */
export interface QuoteVendor {
  id: string
  name: string
}

interface Props {
  eventId: string
  /** The company to ask. Without one (the venue's staff, from the event page), the venue's vendors are offered. */
  vendor?: QuoteVendor | undefined
  onClose: () => void
}

/** The venue's vendors staff may ask, as its directory lists them: approved there for something they still offer, by name. */
function VenueVendorPicker({ venueId, value, onChange }: { venueId: string; value: string; onChange: (vendor: QuoteVendor | null) => void }) {
  const services = useVenueVendorServices(venueId)
  const vendors = (services.data?.vendors ?? [])
    .filter((v) => v.approved.some((s) => s !== 'furniture' && v.offered.includes(s)))
    .sort((a, b) => a.vendorName.localeCompare(b.vendorName, 'en', { sensitivity: 'base' }))
  return (
    <Field label="Vendor" htmlFor="quote-vendor" error={services.isError ? requestErrorMessage(services.error) : undefined}>
      <Select
        id="quote-vendor"
        value={value}
        onChange={(e) => {
          const chosen = vendors.find((v) => v.vendorId === e.target.value)
          onChange(chosen ? { id: chosen.vendorId, name: chosen.vendorName } : null)
        }}
      >
        <option value="">{services.isPending ? 'Loading…' : 'Choose a vendor'}</option>
        {vendors.map((v) => (
          <option key={v.vendorId} value={v.vendorId}>
            {v.vendorName}
          </option>
        ))}
      </Select>
    </Field>
  )
}

/** The company's packages and items, to say which the quote is about. */
function PackagePicker({ vendorId, chosen, onChange, error }: { vendorId: string; chosen: string[]; onChange: (ids: string[]) => void; error?: string | undefined }) {
  const profile = useVendorProfile(vendorId)
  if (profile.isPending) return <LoadingState label="Loading what they offer…" />
  const offerings = profile.data?.offerings ?? []
  if (offerings.length === 0) return null
  return (
    <fieldset className="m-0 space-y-2 border-0 p-0">
      <legend className="mb-2 text-sm font-medium">Packages you&rsquo;re interested in (optional)</legend>
      {offerings.map((o) => (
        <label key={o.id} className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={chosen.includes(o.id)}
            onCheckedChange={(on) => onChange(on ? [...chosen, o.id] : chosen.filter((id) => id !== o.id))}
          />
          <span>
            {o.name} <span className="text-ink-muted">· {offeringPriceLabel(o.price)}</span>
          </span>
        </label>
      ))}
      {error ? <FormError size="sm">{error}</FormError> : null}
    </fieldset>
  )
}

/**
 * Asking a vendor company for a quote for an event (docs/vendor-growth.spec.md,
 * "Quote requests"): the event's facts, the packages it's about if any, and a
 * message. The sender is told first that the company gets their name and
 * email address and can reply directly; once sent, the dialog says where the
 * reply will come.
 */
export function QuoteRequestDialog({ eventId, vendor, onClose }: Props) {
  const { user } = useAuth()
  const event = useEvent(eventId)
  const send = useSendQuoteRequest(eventId)
  const [picked, setPicked] = React.useState<QuoteVendor | null>(vendor ?? null)
  const [offeringIds, setOfferingIds] = React.useState<string[]>([])
  const [message, setMessage] = React.useState('')
  const [errors, setErrors] = React.useState<{ message?: string | undefined; offeringIds?: string | undefined }>({})
  const [sent, setSent] = React.useState(false)

  function submit(e: React.FormEvent): void {
    e.preventDefault()
    if (picked === null) return
    const parsed = createQuoteRequestInputSchema.safeParse({ vendorId: picked.id, offeringIds, message })
    if (!parsed.success) {
      const issue = (field: string) => parsed.error.issues.find((i) => i.path[0] === field)?.message
      setErrors({ message: issue('message'), offeringIds: issue('offeringIds') })
      return
    }
    setErrors({})
    send.mutate(parsed.data, { onSuccess: () => setSent(true) })
  }

  function body() {
    if (event.isPending) return <LoadingState label="Loading the event…" />
    if (event.isError) return <FormError>{requestErrorMessage(event.error)}</FormError>
    const facts = event.data
    if (sent && picked !== null) {
      return (
        <div className="space-y-4">
          <p className="m-0 text-sm">
            Sent to {picked.name}. They&rsquo;ll reply to {user?.email ?? 'your email'}.
          </p>
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </div>
      )
    }
    return (
      <form className="space-y-5" onSubmit={submit} noValidate>
        {vendor === undefined ? (
          <VenueVendorPicker
            venueId={facts.venueId}
            value={picked?.id ?? ''}
            onChange={(chosen) => {
              setPicked(chosen)
              setOfferingIds([])
              // An answer about another vendor isn't about this one.
              send.reset()
            }}
          />
        ) : null}
        <Facts
          layout="stack"
          facts={[
            { label: 'Date', value: facts.date === null ? null : formatEventDate(facts.date), empty: 'Date not set' },
            { label: 'Venue', value: facts.venue.name, empty: 'No venue' },
            { label: 'Guests', value: facts.guestCount > 0 ? String(facts.guestCount) : null, empty: 'Not set yet' },
          ]}
        />
        {picked === null ? null : (
          <PackagePicker vendorId={picked.id} chosen={offeringIds} onChange={setOfferingIds} error={errors.offeringIds} />
        )}
        <Field
          label="Message"
          htmlFor="quote-message"
          error={errors.message}
          hint={`${String(message.length)}/${String(QUOTE_MESSAGE_MAX)}`}
        >
          <Textarea id="quote-message" rows={5} maxLength={QUOTE_MESSAGE_MAX} value={message} onChange={(e) => setMessage(e.target.value)} />
        </Field>
        {picked === null ? null : (
          <p className="m-0 text-xs text-ink-muted">
            {picked.name} will get your name and email address, and can reply to you directly.
          </p>
        )}
        {send.isError ? <FormError>{requestErrorMessage(send.error)}</FormError> : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={picked === null || send.isPending}>
            {send.isPending ? 'Sending…' : 'Send request'}
          </Button>
        </DialogFooter>
      </form>
    )
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{picked === null ? 'Ask a vendor for a quote' : `Ask ${picked.name} for a quote`}</DialogTitle>
          <DialogDescription>They get the event&rsquo;s date, venue and guest count with your message.</DialogDescription>
        </DialogHeader>
        {body()}
      </DialogContent>
    </Dialog>
  )
}

/** "Ask for a quote", opening the dialog for one company or, without one, for the venue's staff to choose. */
export function AskForQuoteButton({
  eventId,
  vendor,
  label = 'Ask for a quote',
  variant = 'outline',
}: {
  eventId: string
  vendor?: QuoteVendor | undefined
  label?: string
  variant?: 'outline' | 'default'
}) {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <Button size="sm" variant={variant} onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open ? <QuoteRequestDialog eventId={eventId} vendor={vendor} onClose={() => setOpen(false)} /> : null}
    </>
  )
}
